<script lang="ts">
  /**
   * Where the fleet actually spends its time.
   *
   * A MapLibre `heatmap` layer over a grid the daemon aggregates in SQL — see `heatmapCells`.
   * The browser never sees a fix: a season for four aircraft is millions of rows and a few
   * thousand cells, so the binning belongs on the server side of the wire.
   *
   * **What the colour means, and it is worth being clear about.** A cell's value is a count of
   * position reports, and reports arrive at a roughly steady rate, so brightness is *time spent
   * there* — not distance flown through. Thermals glow; a fast glide between them is faint. That
   * is the honest answer to "where do they mostly fly", and it is not a route map. The caption
   * under the map says so, because a heatmap that is not explained gets read as one.
   */
  import { Map as MapLibreMap, NavigationControl, ScaleControl } from 'maplibre-gl';
  import type { LngLatBoundsLike, StyleSpecification } from 'maplibre-gl';
  import type { FeatureCollection } from 'geojson';
  import 'maplibre-gl/dist/maplibre-gl.css';
  import DatePicker from './DatePicker.svelte';
  import AirspaceControls from './AirspaceControls.svelte';
  import LoopingPlane from './LoopingPlane.svelte';
  import { collapseAttribution } from './maplibre.ts';
  import { skyFor } from './sky.ts';
  import {
    AIRSPACE_GROUPS,
    AIRSPACE_MAX_ZOOM,
    AIRSPACE_SOURCE,
    AIRPORT_FILTER,
    MAJOR_AIRPORT_FILTER,
    MINOR_AIRPORT_FILTER,
    MINOR_AIRPORT_MIN_ZOOM,
    AIRPORT_SOURCE_LAYER,
    AIRSPACE_SOURCE_LAYER,
    airspaceColour,
    DEFAULT_AIRSPACE_GROUPS,
    enabledFilter,
    limitToMetres,
  } from './airspace.ts';
  import { api } from './api.ts';
  import type { Aircraft, Heatmap, MapConfig } from './api.ts';

  interface Props {
    aircraft: Aircraft[];
  }

  const { aircraft }: Props = $props();

  const SOURCE = 'heat';
  const TERRAIN = 'terrain-dem';
  const STALL_TIMEOUT_MS = 15_000;

  /**
   * Resolution presets rather than a free slider.
   *
   * The numbers are degrees of latitude: 0.005 deg is about 550 m, 0.02 about 2.2 km. A slider
   * invites 0.0005, which is a quarter of a million cells over a season and a payload nobody
   * asked for — and the server clamps it anyway, so the slider would lie.
   */
  const RESOLUTIONS = [
    { label: 'Fein', cell: 0.005 },
    { label: 'Mittel', cell: 0.01 },
    { label: 'Grob', cell: 0.025 },
  ] as const;

  const today = new Date();
  const iso = (d: Date): string => d.toISOString().slice(0, 10);
  const yearsBack = (n: number): string =>
    iso(new Date(Date.UTC(today.getUTCFullYear() - n, today.getUTCMonth(), today.getUTCDate())));

  let selected = $state('');
  let from = $state(yearsBack(1));
  let to = $state(iso(today));
  let cell = $state<number>(0.01);
  let basemap = $state<'satellite' | 'vector'>('vector');
  let showAirspace = $state(false);
  let airGroups = $state(new Set(DEFAULT_AIRSPACE_GROUPS));
  /** Low by default: over southern Germany the volumes stack three and four deep. */
  let airOpacity = $state(0.18);
  /** Übersberg. Flight levels are drawn relative to this — see `limitToMetres`. */
  const REF_ELEVATION_M = 780;
  /** No single flight here, so no weather to reflect — a clear default sky. */
  const weather = {};

  let data = $state<Heatmap | null>(null);
  let config = $state<MapConfig | null>(null);
  let loading = $state(true);
  let error = $state<string | null>(null);

  let container: HTMLDivElement;
  let map: MapLibreMap | undefined;
  let ready = $state(false);
  let stalled = $state(false);
  let failure = $state<string | null>(null);

  const hasLayer = (id: string): boolean => config?.layers.some((l) => l.id === id) ?? false;
  const tileUrl = (layer: string): string =>
    (config?.tileUrlTemplate ?? '/api/v1/tiles/{layer}/{z}/{x}/{y}').replace('{layer}', layer);
  const orthoLayer = $derived(config?.layers.find((l) => l.id === 'ortho'));

  $effect(() => {
    const controller = new AbortController();
    api
      .mapConfig(controller.signal)
      .then((c) => {
        config = c;
      })
      .catch(() => {
        /* The map falls back to its own defaults; the grid is the point here. */
      });
    return () => controller.abort();
  });

  // Re-fetch whenever a control moves. Reading the four values here is what makes this effect
  // depend on them under runes — an abortable request per change, so a fast click sequence
  // cannot land its answers out of order.
  $effect(() => {
    const params = { aircraft: selected, from, to, cell };
    const controller = new AbortController();
    loading = true;
    error = null;

    api
      .heatmap(params, controller.signal)
      .then((result) => {
        data = result;
        loading = false;
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        error = err instanceof Error ? err.message : String(err);
        loading = false;
      });

    return () => controller.abort();
  });

  function toGeoJson(heat: Heatmap): FeatureCollection {
    return {
      type: 'FeatureCollection',
      features: heat.cells.map(([lat, lon, n]) => ({
        type: 'Feature',
        properties: { n },
        geometry: { type: 'Point', coordinates: [lon, lat] },
      })),
    };
  }

  function styleFor(kind: 'satellite' | 'vector'): string | StyleSpecification {
    if (kind === 'vector') {
      // No fallback to the upstream host. There used to be one, and it is the reason the
      // vector basemap was blank: the map is created as soon as its container exists, which is
      // before /map/config has answered, so `config` was null and the map was built with
      // `https://tiles.openfreemap.org/styles/liberty` — a request that returns 0 bytes on a
      // network that cannot reach it. The style then never loaded and the map never recovered,
      // because the creation effect guards on `map !== undefined`.
      //
      // The map now waits for the config instead. `styleFor` is only ever called with one.
      return config?.basemapStyleUrl ?? '';
    }
    return {
      version: 8,
      glyphs: config?.basemapGlyphsUrl ?? '',
      sources: {
        satellite: {
          type: 'raster',
          tiles: [tileUrl('satellite')],
          tileSize: 256,
          maxzoom: 14,
          attribution: config?.layers.find((l) => l.id === 'satellite')?.attribution ?? '',
        },
        // 20 cm orthophotos over Baden-Wuerttemberg, on top of the 10 m global mosaic. The
        // bounds come from the layer catalogue: outside the state the service answers 400, so
        // this must not be asked, and Sentinel-2 shows through there instead.
        ...(orthoLayer === undefined
          ? {}
          : {
              ortho: {
                type: 'raster' as const,
                tiles: [tileUrl('ortho')],
                tileSize: 256,
                maxzoom: 20,
                // Below this the state boundary is in shot and the imagery ends in a straight
                // line across the map, which reads as a rendering fault. Zoomed in you are
                // inside Baden-Wuerttemberg and never see an edge at all.
                minzoom: 9,
                ...(orthoLayer.bounds === null ? {} : { bounds: orthoLayer.bounds }),
                attribution: orthoLayer.attribution,
              },
            }),
      },
      layers: [
        { id: 'background', type: 'background', paint: { 'background-color': '#0d0f14' } },
        { id: 'satellite', type: 'raster', source: 'satellite' },
        ...(orthoLayer === undefined
          ? []
          : [
              {
                id: 'ortho',
                type: 'raster' as const,
                source: 'ortho',
                // Faded in over two zoom levels rather than switched on. The two sources have
                // different colour balance and different sun angles, so a hard swap looks like
                // a seam; a cross-fade reads as the picture simply getting sharper.
                paint: {
                  'raster-opacity': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    9,
                    0,
                    11.5,
                    1,
                  ] as unknown as number,
                },
              },
            ]),
      ],
    } satisfies StyleSpecification;
  }

  /**
   * The heat layer.
   *
   * `heatmap-weight` is normalised against the busiest cell in *this* result rather than a fixed
   * ceiling, so a quiet month and a busy season are both readable. It also means the colours are
   * not comparable between two different queries, which is why the legend says "relativ".
   */
  function addHeat(m: MapLibreMap, heat: Heatmap): void {
    if (m.getSource(SOURCE) !== undefined) {
      m.removeLayer('heat-blur');
      m.removeLayer('heat-points');
      m.removeSource(SOURCE);
    }
    m.addSource(SOURCE, { type: 'geojson', data: toGeoJson(heat) });

    // The 98th percentile, NOT the maximum. The busiest cell is always the home airfield —
    // every flight sits on it for minutes at each end — and scaling to it made the whole map
    // blank with one bright dot. Everything above the percentile simply saturates, which is
    // what a heatmap is supposed to do with an outlier.
    const ceiling = Math.max(1, heat.p98Count);
    m.addLayer({
      id: 'heat-blur',
      type: 'heatmap',
      source: SOURCE,
      paint: {
        'heatmap-weight': ['interpolate', ['linear'], ['get', 'n'], 0, 0, ceiling, 1],
        // Zoomed out, cells overlap and the sum saturates; zoomed in they separate and a single
        // busy cell has to still be visible. Raising the intensity with zoom is what keeps the
        // picture looking like the same data at both ends.
        'heatmap-intensity': ['interpolate', ['linear'], ['zoom'], 5, 0.6, 12, 2.2],
        'heatmap-radius': ['interpolate', ['linear'], ['zoom'], 5, 6, 9, 16, 13, 34],
        'heatmap-opacity': ['interpolate', ['linear'], ['zoom'], 5, 0.85, 13, 0.6],
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          'rgba(12,18,32,0)',
          0.15,
          'rgba(38,92,180,0.55)',
          0.35,
          'rgba(46,178,196,0.75)',
          0.55,
          'rgba(122,214,120,0.85)',
          0.75,
          'rgba(247,204,74,0.9)',
          1,
          'rgba(242,92,60,0.95)',
        ],
      },
    });

    // Individual cells once you are close enough that the blur stops meaning anything.
    m.addLayer({
      id: 'heat-points',
      type: 'circle',
      source: SOURCE,
      minzoom: 11,
      paint: {
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 2, 15, 7],
        'circle-color': 'rgba(247,204,74,0.85)',
        'circle-stroke-color': 'rgba(0,0,0,0.4)',
        'circle-stroke-width': 0.5,
        'circle-opacity': ['interpolate', ['linear'], ['zoom'], 11, 0, 13, 0.7],
      },
    });

    addAirspace(m);
  }

  /**
   * Relief, the same way the flight replay does it.
   *
   * A heatmap of a soaring area without terrain is missing half the explanation: the bright
   * lines run along ridges and the dark patches are valleys, and that is only obvious once the
   * ground has shape. Terrarium encoding, not Mapbox RGB — the AWS Open Data tiles use that,
   * and reading them as RGB produces a landscape of absurd spikes.
   */
  function addTerrain(m: MapLibreMap): void {
    if (!hasLayer('terrain') || m.getSource(TERRAIN) !== undefined) return;
    m.addSource(TERRAIN, {
      type: 'raster-dem',
      tiles: [tileUrl('terrain')],
      tileSize: 256,
      maxzoom: 13,
      encoding: 'terrarium',
    });
    m.setTerrain({ source: TERRAIN, exaggeration: 1.35 });
    m.setSky(skyFor(weather));
  }

  /**
   * openAIP's airspace and airfields, drawn **over** the heat rather than under it.
   *
   * The whole question this page answers is "where do they go", and the answer is much more
   * useful next to the airspace that shapes it — the heat stops at a TMZ floor for a reason.
   * Under the heat the boundaries would be washed out by exactly the cells you want to read
   * them against.
   *
   * Absent entirely when no openAIP key is configured; the button says why rather than being a
   * control that does nothing.
   */
  /**
   * The openAIP source, which carries the airspace **and** the airfields.
   *
   * Added once and kept, independent of the airspace toggle, because the airfields are always
   * shown and they live in the same tile. Tying the source's lifetime to the toggle would make
   * switching airspace off remove every field from the map too.
   */
  function ensureAirspaceSource(m: MapLibreMap): void {
    if (!hasLayer('airspace') || m.getSource(AIRSPACE_SOURCE) !== undefined) return;
    m.addSource(AIRSPACE_SOURCE, {
      type: 'vector',
      tiles: [tileUrl('airspace')],
      // openAIP generates nothing above 12, and an overzoomed vector source loses its geometry
      // entirely — the airspace would vanish the moment you zoomed in.
      maxzoom: AIRSPACE_MAX_ZOOM,
    });
  }

  function addAirspace(m: MapLibreMap): void {
    for (const id of [`${AIRSPACE_SOURCE}-fill`, `${AIRSPACE_SOURCE}-line`]) {
      if (m.getLayer(id) !== undefined) m.removeLayer(id);
    }
    ensureAirspaceSource(m);
    if (!showAirspace || m.getSource(AIRSPACE_SOURCE) === undefined) return;

    const filter = enabledFilter(airGroups);
    const colour = airspaceColour(AIRSPACE_GROUPS);

    m.addLayer({
      id: `${AIRSPACE_SOURCE}-fill`,
      type: 'fill-extrusion',
      source: AIRSPACE_SOURCE,
      'source-layer': AIRSPACE_SOURCE_LAYER,
      filter,
      paint: {
        'fill-extrusion-base': limitToMetres('lower', REF_ELEVATION_M),
        'fill-extrusion-height': limitToMetres('upper', REF_ELEVATION_M),
        'fill-extrusion-color': colour,
        'fill-extrusion-opacity': airOpacity,
      },
    });

    // The outline at ground level, because an extrusion seen from directly above is a flat
    // wash and the boundary is the thing you actually navigate by.
    m.addLayer({
      id: `${AIRSPACE_SOURCE}-line`,
      type: 'line',
      source: AIRSPACE_SOURCE,
      'source-layer': AIRSPACE_SOURCE_LAYER,
      filter,
      paint: { 'line-color': colour, 'line-width': 1.2, 'line-opacity': 0.85 },
    });

    // Re-added last so the fields and their labels stay above the airspace wash.
    addAirports(m);
  }

  /**
   * Airfields, always on and independent of the airspace toggle.
   *
   * They travel in the same openAIP tile as the airspace, so they cost no extra request — and
   * a soaring map without the fields on it is missing the thing every decision is made against.
   * A gliding site gets the accent colour; everything else is neutral, because at a glance the
   * question is "can I get in there" and the answer is different for the two.
   */
  function addAirports(m: MapLibreMap): void {
    for (const id of [
      `${AIRSPACE_SOURCE}-ap-major`,
      `${AIRSPACE_SOURCE}-ap`,
      `${AIRSPACE_SOURCE}-ap-label-major`,
      `${AIRSPACE_SOURCE}-ap-label`,
    ]) {
      if (m.getLayer(id) !== undefined) m.removeLayer(id);
    }
    if (m.getSource(AIRSPACE_SOURCE) === undefined) return;

    const dot = (id: string, filter: typeof AIRPORT_FILTER, minzoom?: number) => {
      m.addLayer({
        id,
        type: 'circle',
        source: AIRSPACE_SOURCE,
        'source-layer': AIRPORT_SOURCE_LAYER,
        filter,
        ...(minzoom === undefined ? {} : { minzoom }),
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 5, 2, 7, 2.8, 12, 5.5],
          // The club's own kind of field in the accent colour: at a glance the question is
          // "can I get in there", and for a gliding site the answer is different.
          'circle-color': ['case', ['==', ['get', 'type'], 'gliding'], '#f7cc4a', '#cfd6e0'],
          'circle-stroke-color': 'rgba(0,0,0,0.6)',
          'circle-stroke-width': 1,
        },
      });
    };

    const label = (id: string, filter: typeof AIRPORT_FILTER, minzoom: number) => {
      m.addLayer({
        id,
        type: 'symbol',
        source: AIRSPACE_SOURCE,
        'source-layer': AIRPORT_SOURCE_LAYER,
        filter,
        minzoom,
        layout: {
          'text-field': ['get', 'name'],
          'text-font': ['Noto Sans Regular'],
          'text-size': ['interpolate', ['linear'], ['zoom'], 6, 9, 9, 10, 13, 13],
          'text-offset': [0, 0.9],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#e7ecf3',
          'text-halo-color': 'rgba(0,0,0,0.75)',
          'text-halo-width': 1.4,
        },
      });
    };

    // Commercial airports at every zoom — they are what anyone orients by on a country-wide
    // view. Everything else only once there is room for it: four thousand airfields at zoom 6
    // is a grey mat with no information in it.
    dot(`${AIRSPACE_SOURCE}-ap-major`, MAJOR_AIRPORT_FILTER);
    dot(`${AIRSPACE_SOURCE}-ap`, MINOR_AIRPORT_FILTER, MINOR_AIRPORT_MIN_ZOOM);
    label(`${AIRSPACE_SOURCE}-ap-label-major`, MAJOR_AIRPORT_FILTER, 6);
    label(`${AIRSPACE_SOURCE}-ap-label`, MINOR_AIRPORT_FILTER, MINOR_AIRPORT_MIN_ZOOM);
  }

  /**
   * Open on where the time was spent, not on the outermost cell.
   *
   * Fitting to the extreme bounds meant a single ferry flight from Rostock decided the view for
   * everything else: the map opened on the whole of Germany and the club's soaring area was a
   * smudge. `focus` is the box holding 98 % of the recorded positions, so a long transit no
   * longer sets the zoom — its cells are still drawn, just off-screen until you zoom out.
   */
  function fit(m: MapLibreMap, heat: Heatmap): void {
    const box = heat.focus ?? heat.bounds;
    if (box === null) return;
    const b: LngLatBoundsLike = [
      [box.minLon, box.minLat],
      [box.maxLon, box.maxLat],
    ];
    m.fitBounds(b, { padding: 48, duration: 0, maxZoom: 11 });
  }

  $effect(() => {
    // Waiting for the config is not a nicety: the style URL lives in it, and a map created
    // without one can never be given a working style afterwards.
    if (container === undefined || map !== undefined || config === null) return;

    const m = new MapLibreMap({
      container,
      style: styleFor(basemap),
      center: [9.2961, 48.4581],
      zoom: 8,
      pitch: 45,
      maxPitch: 80,
      attributionControl: { compact: true },
    });
    // visualizePitch, and a map that can be tilted at all: flat relief is no relief.
    m.addControl(new NavigationControl({ visualizePitch: true }), 'top-right');
    m.addControl(new ScaleControl({ unit: 'metric' }), 'bottom-left');
    collapseAttribution(m);

    const stall = setTimeout(() => {
      if (!ready) stalled = true;
    }, STALL_TIMEOUT_MS);

    m.on('error', (event) => {
      // A missing tile at the edge of coverage is entirely normal — the satellite mosaic stops
      // at zoom 14, the DEM at 13, and the orthophotos at the state border — and must not blank
      // the map. Anything else must be visible, and for a long time it was not: source errors
      // were logged to the console only, so a basemap that failed to load looked identical to
      // one that was merely slow, and the only evidence was a red badge in somebody's DevTools.
      const sourceId = 'sourceId' in event ? (event.sourceId as string | undefined) : undefined;
      const message = event.error?.message ?? String(event);
      const status = (event.error as { status?: number } | undefined)?.status;
      const missingTile = status === 404 || status === 204 || /404/.test(message);

      console.error('maplibre', message, { sourceId, status });
      if (missingTile) return;
      failure = sourceId === undefined ? message : `${sourceId}: ${message}`;
    });

    m.on('load', () => {
      clearTimeout(stall);
      ready = true;
      addTerrain(m);
      ensureAirspaceSource(m);
      addAirports(m);
      if (data !== null) {
        addHeat(m, data);
        fit(m, data);
      }
    });

    map = m;
    return () => {
      clearTimeout(stall);
      m.remove();
      map = undefined;
      ready = false;
    };
  });

  // New data into an already-loaded map.
  $effect(() => {
    const heat = data;
    const m = map;
    if (heat === null || m === undefined || !ready) return;
    addHeat(m, heat);
    fit(m, heat);
  });

  $effect(() => {
    // Reading `showAirspace` here is what subscribes this effect to it.
    const wanted = showAirspace;
    const groups = airGroups;
    const m = map;
    if (m === undefined || !ready) return;
    void wanted;
    void groups;
    addAirspace(m);
  });

  // A style change wipes every source and layer, so the heat has to go back on afterwards.
  $effect(() => {
    const kind = basemap;
    const m = map;
    if (m === undefined || !ready) return;
    m.setStyle(styleFor(kind));
    m.once('styledata', () => {
      // A style change wipes the DEM source along with everything else, so the terrain has to be
      // re-attached before the heat goes back on.
      addTerrain(m);
      ensureAirspaceSource(m);
      addAirports(m);
      if (data !== null) addHeat(m, data);
    });
  });

  const nf = new Intl.NumberFormat('de-DE');
  const hoursApprox = (fixes: number): string => {
    // Fixes arrive at roughly one every four seconds on this feed, so this is an order of
    // magnitude rather than a measurement — hence "rund", and hence no decimals.
    const hours = (fixes * 4) / 3600;
    return hours < 1 ? '<1' : nf.format(Math.round(hours));
  };
</script>

<section class="heat">
  <header class="controls">
    <div class="group" role="group" aria-label="Luftfahrzeug">
      <button class:active={selected === ''} onclick={() => (selected = '')} type="button">
        Alle
      </button>
      {#each aircraft as a (a.id)}
        <button class:active={selected === a.id} onclick={() => (selected = a.id)} type="button">
          {a.registration}
        </button>
      {/each}
    </div>

    <div class="group dates">
      <DatePicker label="von" bind:value={from} max={to} />
      <DatePicker label="bis" bind:value={to} min={from} />
    </div>

    <div class="group" role="group" aria-label="Auflösung">
      {#each RESOLUTIONS as r (r.cell)}
        <button class:active={cell === r.cell} onclick={() => (cell = r.cell)} type="button">
          {r.label}
        </button>
      {/each}
    </div>

    <div class="group" role="group" aria-label="Kartenhintergrund">
      <button class:active={basemap === 'vector'} onclick={() => (basemap = 'vector')} type="button">
        Karte
      </button>
      <button
        class:active={basemap === 'satellite'}
        onclick={() => (basemap = 'satellite')}
        type="button">Satellit</button
      >
    </div>

    <div class="group">
      {#if hasLayer('airspace')}
        <button
          class:active={showAirspace}
          onclick={() => (showAirspace = !showAirspace)}
          type="button">Lufträume</button
        >
      {:else}
        <button class="off" type="button" disabled title="Kein openAIP-Key konfiguriert">
          Lufträume
        </button>
      {/if}
    </div>
  </header>

  {#if showAirspace && hasLayer('airspace')}
    <AirspaceControls
      bind:groups={airGroups}
      bind:opacity={airOpacity}
      hint={`Höhen als Volumen: GND-Untergrenzen exakt, Flugflächen relativ zu ${REF_ELEVATION_M} m — die Beschriftung im Popup ist maßgeblich.`}
    />
  {/if}

  <div class="canvas">
    <div class="map" bind:this={container}></div>

    {#if loading}
      <div class="overlay">
        <LoopingPlane width={120} seconds={4} label="Lädt" />
        <p>Heatmap wird gerechnet …</p>
      </div>
    {:else if error !== null || failure !== null}
      <div class="overlay">
        <p class="bad">{error ?? failure}</p>
      </div>
    {:else if data !== null && data.cells.length === 0}
      <div class="overlay">
        <p>Keine Flüge in diesem Zeitraum.</p>
      </div>
    {:else if stalled && !ready}
      <div class="overlay">
        <p>
          Die Karte hat noch nicht gezeichnet. Das passiert, wenn der Browser diesen Tab nicht
          darstellt — die Renderschleife hängt an <code>requestAnimationFrame</code>. Tab in den
          Vordergrund holen und neu laden.
        </p>
      </div>
    {/if}
  </div>

  {#if data !== null && data.cells.length > 0}
    <footer class="legend">
      <p class="numbers">
        <strong>{nf.format(data.flights)}</strong> Flüge ·
        <strong>{nf.format(data.fixes)}</strong> Positionen · rund
        <strong>{hoursApprox(data.fixes)}</strong> Stunden in der Luft ·
        Raster {data.cellDeg}°
      </p>
      <div class="scale" aria-hidden="true">
        <span>selten</span>
        <span class="ramp"></span>
        <span>oft</span>
      </div>
      <p class="caveat">
        Die Helligkeit ist <strong>verweilte Zeit</strong>, nicht geflogene Strecke: ein Bart
        leuchtet, ein schneller Gleitflug bleibt blass. Die Skala endet beim 98. Perzentil
        ({nf.format(data.p98Count)} Positionen je Feld) und nicht beim Maximum — das ist immer
        der Heimatplatz, auf dem jeder Flug an beiden Enden minutenlang steht, und dagegen
        normiert wäre der Rest der Karte unsichtbar. Zwei Abfragen sind damit nicht miteinander
        vergleichbar. Und wo kein OGN-Empfänger steht, sieht ungehört aus wie nie beflogen.
      </p>
    </footer>
  {/if}
</section>

<style>
  .heat {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .controls {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 0.75rem;
    align-items: center;
  }

  .group {
    display: flex;
    gap: 0.25rem;
    background: var(--surface-2, #161a22);
    border-radius: 999px;
    padding: 0.2rem;
  }

  .group button {
    border: 0;
    background: transparent;
    color: var(--text-dim, #9aa4b5);
    font: inherit;
    font-size: 0.85rem;
    padding: 0.35rem 0.75rem;
    border-radius: 999px;
    cursor: pointer;
  }

  .group button.off {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .group button.active {
    background: var(--accent, #2eb2c4);
    color: #04121a;
    font-weight: 600;
  }

  .dates {
    border-radius: 999px;
    padding: 0.2rem;
    gap: 0.15rem;
  }

  .canvas {
    position: relative;
    border-radius: 0.9rem;
    overflow: hidden;
    /* Tall on a phone, wide on a desktop: a heatmap of a region is unreadable in a letterbox.
       `svh` for the same reason as the flight map — see the note there. */
    height: min(72vh, 620px);
    height: min(72svh, 620px);
    background: #0d0f14;
  }

  .map {
    position: absolute;
    inset: 0;
  }

  .overlay {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 1.5rem;
    background: rgba(13, 15, 20, 0.82);
    color: var(--text-dim, #9aa4b5);
    font-size: 0.9rem;
  }

  .overlay p {
    margin: 0;
    max-width: 42ch;
  }

  .overlay .bad {
    color: #f25c3c;
  }

  .legend {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .numbers {
    margin: 0;
    font-size: 0.9rem;
    color: var(--text-dim, #9aa4b5);
  }

  .numbers strong {
    color: var(--text, #e7ecf3);
  }

  .scale {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.75rem;
    color: var(--text-dim, #9aa4b5);
  }

  .ramp {
    flex: 1;
    max-width: 260px;
    height: 8px;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      rgba(38, 92, 180, 0.55),
      rgba(46, 178, 196, 0.75),
      rgba(122, 214, 120, 0.85),
      rgba(247, 204, 74, 0.9),
      rgba(242, 92, 60, 0.95)
    );
  }

  .caveat {
    margin: 0;
    font-size: 0.78rem;
    line-height: 1.5;
    color: var(--text-dim, #9aa4b5);
    max-width: 70ch;
  }

  @media (max-width: 600px) {
    .controls {
      gap: 0.4rem;
    }
    .group button {
      padding: 0.3rem 0.6rem;
      font-size: 0.8rem;
    }
  }

  /*
   * Narrow and touch are different questions, and the rule above only answered the first.
   *
   * It shrinks the buttons at 600 px — sensible when the constraint is width — and a phone is
   * both narrow *and* touched, so the controls that most need a thumb were the smallest on the
   * page. Height comes back here; the horizontal padding stays tight, because that is what was
   * actually in short supply.
   */
  @media (pointer: coarse) {
    .group button {
      min-height: 40px;
    }
  }
</style>
